import { useCallback, useEffect, useState } from 'react';
import { useNotify } from 'react-admin';
import {
	AgentDefinition,
	DefinitionView,
	RoleView,
	getDefinition,
	getOrg,
	resetDefinition,
	saveDefinition,
} from '../../Services/agents';

// Hook de l'écran Organisation : l'arbre des rôles, le rôle sélectionné avec sa
// définition complète (effective + code + versions), et les deux gestes admin.
// #region useAgentOrg
export const useAgentOrg = () => {
	const notify = useNotify();
	const [roles, setRoles] = useState<RoleView[]>([]);
	const [selected, setSelected] = useState<string>('');
	const [detail, setDetail] = useState<DefinitionView | null>(null);
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	const loadOrg = useCallback(async () => {
		try {
			const res = await getOrg();
			setRoles(res.roles || []);
			setSelected((cur) => cur || res.roles?.[0]?.role.slug || '');
		} catch (e: any) {
			notify(`Organisation : ${e?.message || e}`, { type: 'error' });
		} finally {
			setLoading(false);
		}
	}, [notify]);

	const loadDetail = useCallback(
		async (slug: string) => {
			if (!slug) return;
			// Vider d'abord : sinon l'éditeur affiche l'ancien rôle sous la nouvelle
			// sélection pendant la latence, et un clic rapide sur « Enregistrer »
			// écrirait la définition du rôle A sur le rôle B.
			setDetail(null);
			try {
				setDetail(await getDefinition(slug));
			} catch (e: any) {
				notify(`Définition : ${e?.message || e}`, { type: 'error' });
			}
		},
		[notify],
	);

	useEffect(() => {
		loadOrg();
	}, [loadOrg]);
	useEffect(() => {
		loadDetail(selected);
	}, [selected, loadDetail]);

	// Enregistre une NOUVELLE version (jamais d'écrasement) puis recharge arbre + détail.
	const save = async (def: AgentDefinition) => {
		setSaving(true);
		try {
			await saveDefinition(selected, def);
			notify('Nouvelle version enregistrée', { type: 'success' });
			await Promise.all([loadOrg(), loadDetail(selected)]);
		} catch (e: any) {
			notify(e?.error || e?.message || 'Enregistrement impossible', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	const reset = async () => {
		setSaving(true);
		try {
			await resetDefinition(selected);
			notify('Définition revenue aux valeurs du code', { type: 'info' });
			await Promise.all([loadOrg(), loadDetail(selected)]);
		} catch (e: any) {
			notify(e?.error || e?.message || 'Réinitialisation impossible', { type: 'error' });
		} finally {
			setSaving(false);
		}
	};

	return { roles, selected, setSelected, detail, loading, saving, save, reset };
};
// #endregion
